// Message framing for data channels: every message becomes one or more binary frames so large
// payloads fit SCTP's safe message size and text/binary share one format.
//
//   frame = [u32 messageId][u16 index][u16 count][u8 flags] payload
//   flags bit 0: payload is UTF-8 text

export const HEADER_BYTES = 9;
/** Largest frame we send: the interoperable SCTP message size across browsers. */
export const MAX_FRAME_BYTES = 16 * 1024;
export const MAX_CHUNK_BYTES = MAX_FRAME_BYTES - HEADER_BYTES;
/** Refuse to reassemble anything bigger (protects against hostile peers). */
export const MAX_MESSAGE_BYTES = 64 * 1024 * 1024;
/** Bound on bytes held across all incomplete messages (a peer can't make us buffer more). */
export const MAX_BUFFERED_BYTES = 2 * MAX_MESSAGE_BYTES;

const TEXT_FLAG = 1;
const encoder = new TextEncoder();
const decoder = new TextDecoder();

export type Message = string | Uint8Array;

export function encodeMessage(messageId: number, message: Message): Uint8Array[] {
  const isText = typeof message === "string";
  const bytes = isText ? encoder.encode(message) : message;
  if (bytes.byteLength > MAX_MESSAGE_BYTES) throw new Error("message too large");
  const count = Math.max(1, Math.ceil(bytes.byteLength / MAX_CHUNK_BYTES));
  const frames: Uint8Array[] = [];
  for (let index = 0; index < count; index++) {
    const chunk = bytes.subarray(index * MAX_CHUNK_BYTES, (index + 1) * MAX_CHUNK_BYTES);
    const frame = new Uint8Array(HEADER_BYTES + chunk.byteLength);
    const view = new DataView(frame.buffer);
    view.setUint32(0, messageId >>> 0);
    view.setUint16(4, index);
    view.setUint16(6, count);
    view.setUint8(8, isText ? TEXT_FLAG : 0);
    frame.set(chunk, HEADER_BYTES);
    frames.push(frame);
  }
  return frames;
}

interface Partial {
  chunks: (Uint8Array | undefined)[];
  received: number;
  bytes: number;
  isText: boolean;
}

/** Reassembles frames into messages. Tolerates out-of-order frames (unordered channels). */
export class Reassembler {
  readonly #partials = new Map<number, Partial>();
  /** Bound on simultaneously incomplete messages (unordered channels can drop chunks). */
  readonly #maxPartials: number;
  readonly #maxBufferedBytes: number;
  #bufferedBytes = 0;

  constructor(maxPartials = 64, maxBufferedBytes = MAX_BUFFERED_BYTES) {
    this.#maxPartials = maxPartials;
    this.#maxBufferedBytes = maxBufferedBytes;
  }

  /** Bytes currently held for incomplete messages. */
  get bufferedBytes(): number {
    return this.#bufferedBytes;
  }

  #drop(messageId: number): void {
    const partial = this.#partials.get(messageId);
    if (!partial) return;
    this.#bufferedBytes -= partial.bytes;
    this.#partials.delete(messageId);
  }

  /** Returns the completed message, or undefined if more frames are needed or the frame is bad. */
  push(frame: Uint8Array): Message | undefined {
    if (frame.byteLength < HEADER_BYTES) return undefined;
    const view = new DataView(frame.buffer, frame.byteOffset, frame.byteLength);
    const messageId = view.getUint32(0);
    const index = view.getUint16(4);
    const count = view.getUint16(6);
    const isText = (view.getUint8(8) & TEXT_FLAG) !== 0;
    const payload = frame.subarray(HEADER_BYTES);
    if (count === 0 || index >= count || payload.byteLength > MAX_CHUNK_BYTES) return undefined;

    if (count === 1) return isText ? decoder.decode(payload) : payload.slice();

    let partial = this.#partials.get(messageId);
    if (!partial) {
      if (count * MAX_CHUNK_BYTES > MAX_MESSAGE_BYTES + MAX_CHUNK_BYTES) return undefined;
      if (this.#partials.size >= this.#maxPartials) {
        this.#drop(this.#partials.keys().next().value as number); // drop the oldest
      }
      partial = { chunks: new Array(count), received: 0, bytes: 0, isText };
      this.#partials.set(messageId, partial);
    }
    if (partial.chunks.length !== count || partial.chunks[index]) return undefined;
    if (partial.bytes + payload.byteLength > MAX_MESSAGE_BYTES) {
      this.#drop(messageId);
      return undefined;
    }
    // Over the total bound: give up on the oldest incomplete messages (possibly this one).
    while (
      this.#bufferedBytes + payload.byteLength > this.#maxBufferedBytes &&
      this.#partials.size
    ) {
      this.#drop(this.#partials.keys().next().value as number);
    }
    if (!this.#partials.has(messageId)) return undefined;
    partial.chunks[index] = payload.slice();
    partial.received++;
    partial.bytes += payload.byteLength;
    this.#bufferedBytes += payload.byteLength;
    if (partial.received < count) return undefined;

    this.#drop(messageId);
    const whole = new Uint8Array(partial.bytes);
    let offset = 0;
    for (const chunk of partial.chunks as Uint8Array[]) {
      whole.set(chunk, offset);
      offset += chunk.byteLength;
    }
    return partial.isText ? decoder.decode(whole) : whole;
  }
}
